// Work item type and priority marks, and the Work page's view glyphs.
import type { WorkPriority, WorkType } from './workApi';
import { PRIORITY_LABEL, TYPE_LABEL } from './model';

const box = { width: 16, height: 16, viewBox: '0 0 16 16' } as const;

export function TypeMark({ type }: { type: WorkType }): JSX.Element {
  const label = TYPE_LABEL[type];
  switch (type) {
    case 'epic':
      return (
        <svg {...box} role="img" aria-label={label}>
          <rect x="1" y="1" width="14" height="14" rx="3" fill="var(--warning-bg)" />
          <path d="M9 3 4.5 9H8l-1 4 4.5-6H8z" fill="var(--brand-accent)" />
        </svg>
      );
    case 'feature':
      return (
        <svg {...box} role="img" aria-label={label}>
          <rect x="1" y="1" width="14" height="14" rx="3" fill="var(--success-bg)" />
          <path d="M5 3.5h6v9l-3-2.2-3 2.2z" fill="var(--success-solid)" />
        </svg>
      );
    case 'bug':
      return (
        <svg {...box} role="img" aria-label={label}>
          <rect x="1" y="1" width="14" height="14" rx="3" fill="var(--danger-bg)" />
          <circle cx="8" cy="8" r="3.2" fill="var(--danger-solid)" />
        </svg>
      );
    case 'subtask':
      return (
        <svg {...box} role="img" aria-label={label}>
          <rect x="1.5" y="1.5" width="13" height="13" rx="3" fill="none" stroke="var(--border-strong)" />
          <path d="M5 4.5v5h5" fill="none" stroke="var(--fg-muted)" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="10.5" cy="9.5" r="1.6" fill="var(--fg-muted)" />
        </svg>
      );
    default:
      return (
        <svg {...box} role="img" aria-label={label}>
          <rect x="1" y="1" width="14" height="14" rx="3" fill="var(--accent-soft)" />
          <path d="m4.5 8.2 2.3 2.3 4.7-5" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
  }
}

const PRIORITY_PATHS: Record<WorkPriority, { d: string; stroke: string }> = {
  highest: { d: 'M3 9.5 8 5l5 4.5M3 13 8 8.5l5 4.5', stroke: 'var(--danger-solid)' },
  high: { d: 'M3 11 8 6.5l5 4.5', stroke: 'var(--danger-solid)' },
  medium: { d: 'M3.5 6.5h9M3.5 10h9', stroke: 'var(--warning-solid)' },
  low: { d: 'M3 6 8 10.5 13 6', stroke: 'var(--info-solid)' },
  lowest: { d: 'M3 3.5 8 8l5-4.5M3 7.5 8 12l5-4.5', stroke: 'var(--info-solid)' },
};

export function PriorityMark({ priority }: { priority: WorkPriority }): JSX.Element {
  const p = PRIORITY_PATHS[priority];
  return (
    <svg {...box} fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label={`${PRIORITY_LABEL[priority]} priority`}>
      <title>{PRIORITY_LABEL[priority]}</title>
      <path d={p.d} stroke={p.stroke} />
    </svg>
  );
}

const glyph = { width: 15, height: 15, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, 'aria-hidden': true } as const;

export const ListGlyph = () => <svg {...glyph}><path d="M2.5 4h11M2.5 8h11M2.5 12h11" /></svg>;
export const BoardGlyph = () => (
  <svg {...glyph}><rect x="2" y="2.5" width="3.4" height="11" rx="1" /><rect x="6.3" y="2.5" width="3.4" height="7" rx="1" /><rect x="10.6" y="2.5" width="3.4" height="9" rx="1" /></svg>
);
export const TimesheetGlyph = () => <svg {...glyph}><rect x="2" y="3" width="12" height="11" rx="1.5" /><path d="M2 6.5h12M5.5 1.8v2.4M10.5 1.8v2.4" /></svg>;
export const RelatedGlyph = () => (
  <svg {...glyph}><circle cx="4" cy="8" r="2" /><circle cx="12" cy="4" r="2" /><circle cx="12" cy="12" r="2" /><path d="m5.8 7.1 4.4-2.2M5.8 8.9l4.4 2.2" /></svg>
);
export const FilesGlyph = () => <svg {...glyph} strokeLinejoin="round"><path d="M4 1.8h5l3.5 3.5v8.9H4z" /><path d="M9 1.8v3.5h3.5" /></svg>;
export const TeamGlyph = () => (
  <svg {...glyph}><circle cx="6" cy="5.5" r="2.5" /><path d="M1.8 13.5c.6-2.4 2.3-3.7 4.2-3.7s3.6 1.3 4.2 3.7" /><path d="M10.5 3.2a2.3 2.3 0 0 1 0 4.4M12 9.9c1.1.5 1.9 1.7 2.2 3.6" /></svg>
);
export const MyWorkGlyph = () => <svg {...glyph}><circle cx="8" cy="5.5" r="2.8" /><path d="M2.8 14c.7-2.7 2.7-4.2 5.2-4.2s4.5 1.5 5.2 4.2" /></svg>;
export const AllWorkGlyph = () => (
  <svg {...glyph} strokeLinejoin="round"><rect x="2" y="2" width="5" height="5" rx="1" /><rect x="9" y="2" width="5" height="5" rx="1" /><rect x="2" y="9" width="5" height="5" rx="1" /><rect x="9" y="9" width="5" height="5" rx="1" /></svg>
);
export const GroupGlyph = () => <svg {...glyph} strokeLinejoin="round"><path d="M8 2 14 5 8 8 2 5z" /><path d="m2 8 6 3 6-3M2 11l6 3 6-3" /></svg>;
export const PlusGlyph = () => <svg {...glyph} strokeWidth={1.8} strokeLinecap="round"><path d="M8 3v10M3 8h10" /></svg>;
export const ExternalGlyph = () => (
  <svg {...glyph} width={13} height={13} strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2.5h4v4M13.5 2.5 7 9M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" /></svg>
);
export const CopyGlyph = () => <svg {...glyph}><rect x="5" y="5" width="8.5" height="8.5" rx="1.5" /><path d="M3 10.5V3.8C3 3.3 3.3 3 3.8 3h6.7" /></svg>;
